import { mapCurrency } from '@webapp/core/data-access/mappers/mapCurrency'
import { type NextApproverChoiceConstructor } from '@webapp/modules/expense/entities/NextApproverChoice'
import { type TravelAddressConstructor } from '@webapp/modules/expense/entities/TravelAddress'
import { type TravelApprovalLogConstructor } from '@webapp/modules/expense/entities/TravelApprovalLog'
import { type TravelDestinationConstructor } from '@webapp/modules/expense/entities/TravelDestination'
import { type TravelRequestSummaryConstructor } from '@webapp/modules/expense/entities/TravelRequestSummary'
import {
  type TravelAddressApiModel,
  type TravelApprovalLogApiModel,
  type TravelDestinationApiModel,
  type TravelNextApproverChoiceApiModel,
  type TravelRequestSummaryApiModel,
} from '../api-models/TravelRequestSummaryApiModel'

const mapTravelAddress = (
  address: TravelAddressApiModel
): TravelAddressConstructor => ({
  id: address.id,
  name: address.name ?? '',
  addressLineOne: address.addressLineOne ?? '',
  city: address.city ?? null,
  stateProvince: address.state_province ?? null,
  country: address.country ?? null,
  postalCode: address.postalCode ?? '',
})

const mapTravelDestination = (
  destination: TravelDestinationApiModel
): TravelDestinationConstructor => ({
  id: destination.id,
  sequence: destination.sequence,
  transport: destination.Transport,
  departurePlaceId: destination.departure,
  departureReference: destination.departureReference ?? '',
  departureDate: destination.departureDate,
  departureAddress: mapTravelAddress(destination.departureAddress),
  arrivalPlaceId: destination.arrival,
  arrivalReference: destination.arrivalReference ?? '',
  arrivalDate: destination.arrivalDate,
  arrivalAddress: mapTravelAddress(destination.arrivalAddress),
})

const mapTravelApprovalLog = (
  log: TravelApprovalLogApiModel
): TravelApprovalLogConstructor => ({
  id: log.id,
  action: log.action,
  dateTime: log.dateTime,
  user: {
    firstName: log.user.firstName,
    lastName: log.user.lastName,
  },
  nextApprover: log.next_approver
    ? {
        firstName: log.next_approver.firstName,
        lastName: log.next_approver.lastName,
      }
    : null,
  oldTravelBudget: Number(log.oldTravelBudget),
  newTravelBudget: Number(log.newTravelBudget),
})

const mapTravelNextApproverChoice = (
  choice: TravelNextApproverChoiceApiModel
): NextApproverChoiceConstructor => ({
  id: choice.id,
  firstName: choice.firstName,
  lastName: choice.lastName,
  levelText: choice.level_text || null,
  approvalDelegatee: choice.approval_delegatee
    ? {
        id: choice.approval_delegatee.id,
        firstName: choice.approval_delegatee.firstName,
        lastName: choice.approval_delegatee.lastName,
        expectedReturnDate: choice.expected_return_date || null,
      }
    : null,
})

export const mapTravelRequestSummaryApiModel = (
  summary: TravelRequestSummaryApiModel
): TravelRequestSummaryConstructor => ({
  id: summary.id,
  uuid: summary.uuid,
  status: summary.status,
  travelBudget: Number(summary.travelBudget),
  currency: mapCurrency(summary.currency),
  dateCreated: summary.dateCreated,
  dateLastModified: summary.dateLastModified,
  requester: summary.requester,
  nextApprover: summary.next_approver,
  primaryCategory: summary.branch,
  secondaryCategory: summary.department,
  destinations: summary.destinations.map(mapTravelDestination),
  logs: summary.logs.map(mapTravelApprovalLog),
  nextApproverChoices: summary.next_approver_choices
    ? summary.next_approver_choices.map(mapTravelNextApproverChoice)
    : null,
  delegatedBy: summary.delegated_by || null,
})
